import { t } from 'i18n';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  ModalHeader,
  ModalBody,
  Button,
  Modal,
  styled,
  ModalFooter,
  SvgIcon,
  InfoToast,
  ThemeProps,
} from '@teko/ui';
import {
  AddToCartReq,
  Cart,
  CartContextStateSuggestedCoupon,
  UpdateOrderLineReq,
  DeliveryMethod,
  CartOrderLine,
  CartSellerLine,
  CartGift,
  PartOrderPromotion,
  PartOrderBenefit,
  DisplayPolicy,
  AppliedServices,
} from '../types/cart';
import {
  CustomerInfo,
  ApplyServiceCart,
  ApplyPaymentMethodCart,
  TECHNICAL_SUPPORT_SERVICE_ID,
  CouponInfo,
  ScheduledDelivery,
} from '@teko/cart';
import { useTenantConfig } from '../hooks';
import { cookCart, groupCartBySeller } from '../helpers/cart';
import { useToast } from './';
import { useAuth } from '@teko/auth-react';
import {
  trackChangeQuantityProduct,
  trackDecreaseQuantityProduct,
  trackIncreaseQuantityProduct,
  trackManually,
  trackRemoveFromCart,
  trackSelectProduct,
  getComboBenefits,
} from '../helpers';
import { useGlobalData } from './GlobalDataContext';
import { ApplicableService } from 'types';
import { useServices } from './ServiceContext';
import { useScreen } from './ScreenContext';
import { formatCurrency } from 'helpers/common';
import { LOCALSTORAGE_CART_APPLY_COUPON } from '../constants';
import { logger } from '@teko/log';
import { setCartToken } from '@teko/cart';
import { Address } from '@teko/user';
import { ApiError } from '@teko/api';
import { useToast as useDesktopToast } from '@teko/ds-desktop';
import { useToast as useMobileToast } from '@teko/ds-mobile';

export interface CartProviderProps {
  children: React.ReactNode;
}
export interface PromotionAward {
  gifts: CartGift[];
  voucher?: CartVoucher;
}
export interface ShippingFeeProps {
  serviceId: number;
  applyType: string;
  discount: number;
}
export interface CartVoucher {
  quantity: number;
}
export interface PromotionsMap {
  [lineItemId: string]: PromotionAward;
}
export interface CouponConfirmStatusResult {
  message: string;
  show: boolean;
}
interface BillingInfo {
  taxCode: string;
  name: string;
  address: string;
  email: string;
}
interface ShippingInfo extends Address {
  storeCode?: string;
  fullAddress?: string;
  expectedReceiveFrom?: Date;
}
export type OrderData = Cart & {
  promotionsMap: PromotionsMap;
  sellerLines: CartSellerLine[];
  deliveryMethod: DeliveryMethod;
  eVouchersDiscount: number;
  paymentMethodCode: string;
  shippingFeeDiscount: number;
  shippingInfo?: ShippingInfo & ScheduledDelivery;
  billingInfo?: BillingInfo;
  orderId?: string;
  isClone?: boolean;
  termsAndConditions?: string;
};
interface SubstituteCouponModalProps {
  show?: boolean;
  body?: React.ReactNode;
  onApply?: () => void;
}
export interface GiftsImgMap {
  [sku: string]: string;
}
interface AppliedCoupon extends CouponInfo {
  amount: number;
  appliedAmount?: number;
}
export interface CartContextState {
  applicablePolicies: DisplayPolicy[];
  appliedServices: AppliedServices[];
  minOrderAmountToCheckout?: number;
  preCalculatedPoints?: number;
  order?: OrderData;
  paymentMethodCode: string;
  totalCartItemsSelected: number;
  loading: boolean;
  loadingAddToCart: boolean;
  suggestedCoupons?: CartContextStateSuggestedCoupon[];
  partOrderPromotions?: PartOrderPromotion[];
  totalPartOrderDiscount: number;
  partOrderDiscountText: string;
  selectedPartOrderPromotion?: PartOrderPromotion;
  giftsImgMap: GiftsImgMap;
  appliedCoupons?: AppliedCoupon[];
  appliedEVouchers?: AppliedCoupon[];
  isAllowChangePrice: boolean;
  isEmpty?: boolean;
  cartDiscountLoading: boolean;
  comboProducts: CartOrderLine[];
  totalSelectedComboProducts: number;
  comboGrandTotal: number;
  onApplyCoupon: (coupon: string) => void;
  comboBenefits: PartOrderBenefit[];
  confirmPartOrderPromotion: (
    coupon: string,
    override?: boolean,
    onSuccess?: () => void,
  ) => void;
  onRemoveCoupon: (payload?: boolean | string) => Promise<void>;
  onAddToCart: (body: AddToCartReq, fromProductCard?: boolean) => Promise<any>;
  onAddMultiToCart: (body: AddToCartReq[]) => Promise<any>;
  onUpdateOrderLine: (id: string, body: UpdateOrderLineReq) => Promise<any>;
  onRemoveItem: (orderLine: CartOrderLine) => Promise<any>;
  onIncrease: (orderLine: CartOrderLine) => Promise<any>;
  onDecrease: (orderLine: CartOrderLine) => Promise<any>;
  onSelectOrderLine: (orderLine: CartOrderLine) => Promise<any>;
  onSelectCombo: () => void;
  onChangeQuantity: (
    orderLine: CartOrderLine,
    newQuantity: number,
  ) => Promise<any>;
  onDeleteCart: () => Promise<any>;
  onSelectSeller: (sellerId: number, selected: boolean) => Promise<any>;
  refetch: (withLoading?: boolean) => void;
  customer?: CustomerInfo;
  applyServiceToCart: (body?: ApplyServiceCart) => Promise<void>;
  applyPaymentMethodToCard: (body?: ApplyPaymentMethodCart) => Promise<void>;
  applyShippingAddress: (addr: Address) => Promise<void>;
  installService?: AppliedServices;
  appliedDeliveryService: AppliedServices;
  subTotalAfterLineDiscount: number;
  totalServiceFee: number;
  totalPriceDifferences: number;
}
const CustomModal = styled(Modal)`
  margin: 16px;
`;
const ApplyCouponModal = styled(Modal)`
  padding: 1.5rem;
  margin: 1rem;
  border-radius: 0.5rem;
`;
const StyledModalHeader = styled.p`
  padding: 0;
  margin: 0;
  text-align: center;
  font-weight: bold;
  font-size: 0.9375rem;
  line-height: 1.5rem;
  color: #1b1d29;
`;
const StyledModalBody = styled(ModalBody)(
  ({ theme: { colors } }: ThemeProps) => `
    padding: 0;
    margin-top: 0.5rem;
    margin-bottom: 1.25rem;
    font-size: 0.8125rem;
    font-weight: 400;
    color: ${colors.text};
    text-align: center;
  `,
);
const StyledModalFooter = styled(ModalFooter)`
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const StyledSecondaryButton = styled(Button)(
  ({ theme: { colors } }: ThemeProps) => `
    padding: 0.5rem;
    border-radius: 0.25rem;
    border: 1px solid #bcbfd6;
    line-height: 1.5rem;
    font-weight: 500;
    font-size: 0.9375rem;
    color: ${colors.text};
    margin-right: 0.75rem;
    flex: 1;
    background-color: white;
  `,
);
const StyledPrimaryButton = styled(Button)`
  padding: 0.5rem;
  border-radius: 0.25rem;
  line-height: 1.5rem;
  font-weight: 500;
  font-size: 0.9375rem;
  color: white;
  flex: 1;
`;
const CartContext = React.createContext<CartContextState | undefined>(
  undefined,
);
const StyledLeftNotification = styled.span`
  margin-right: 12px;
`;
const StyledRightNotification = styled.span`
  font-weight: 500;
  text-transform: uppercase;
  border-left: 1px solid;
  display: inline-flex;
  align-items: center;
  padding-left: 12px;
`;
const StyledSvgIcon = styled(SvgIcon)`
  margin-right: 8px;
`;
const CouponErrorMessage = styled.div`
  span {
    color: rgb(255, 255, 255);
    display: inline;
    background-color: rgb(239, 39, 65);
    padding: 0px 0.4rem;
    border-radius: 2px;
    font-size: 14px;
  }
`;
const StyledButton = styled(Button)`
  height: 36px;
  padding: 0 20px;
`;
export const CONSULTATION_SERVICE_ID = 6;
const PROMOTION_OVERRIDE_COUPON_CODE = 400203;
const COUPON_OVERRIDE_PROMOTION_CODE = 400202;
const COUPON_CONFLICT_PART_ORDER_AND_COMBO_CODES = [400204, 400205];
const PLATFORM_NOT_ALLOW_MULTI_EVOUCHER_CODE = 400206;
export const CartProvider: React.FunctionComponent<CartProviderProps> = ({
  children,
}: CartProviderProps) => {
  const router = useRouter();
  const { isMobile, baseDomain } = useGlobalData();
  const { cartService, discoveryService } = useServices();
  const { trackingScreenName } = useScreen();
  const { showToast } = useToast();
  const desktopToast = useDesktopToast();
  const mobileToast = useMobileToast();
  const [couponError, setCouponError] = useState('');
  const [cart, setCart] = useState<OrderData>();
  const [loadingCart, setLoadingCart] = useState(true);
  const [loadingCartDiscount, setLoadingCartDiscount] = useState(false);
  const [loadingAddToCart, setLoadingAddToCart] = useState(false);
  const [allowChangePriceProducts, setAllowChangePriceProducts] = useState<
    string[]
  >([]);
  const [isPostLoginRefetch, setIsPostLoginRefetch] = useState<boolean>(false);
  const {
    channel,
    terminal,
    changePriceFeature,
    forceLoginBeforeApplyCoupon,
    location,
  } = useTenantConfig();
  const [giftsImgMap, setGiftsImgMap] = useState<GiftsImgMap>({});
  const { user, loading: isFetchingIAMState, authService } = useAuth();
  const [substituteCouponModal, setSubstituteCouponModal] =
    useState<SubstituteCouponModalProps>({ show: false });
  const userAddress = user?.address;
  const defaultParamsCart = {
    channel,
    terminal,
    location: location || undefined,
  };
  const [skuImageMapping, setSkuImageMapping] = useState<{
    [k: string]: string;
  }>({});

  useEffect(() => {
    async function fetchMapping() {
      if (!cart) return;
      const noImageSkus = cart.orderLines
        ?.filter((orderLine) => !orderLine.product?.productInfo?.imageUrl)
        .map((orderLine) => orderLine.sku)
        .concat(
          Object.values(cart.promotionsMap || {})
            .reduce((prev, curr) => ({ ...prev, ...curr.gifts }), [])
            .filter((item) => !item.imageUrl)
            .map((item) => item.sku),
        );

      if (noImageSkus.length) {
        const products = await discoveryService.getProductDetailBySkus({
          skus: noImageSkus,
          terminalCode: terminal,
        });

        setSkuImageMapping(
          products.reduce(
            (prev: { [k: string]: string }, curr: any) => ({
              ...prev,
              [curr.productInfo?.sku]: curr.productInfo?.imageUrl,
            }),
            {},
          ),
        );
      }
    }
    fetchMapping();
  }, [cart]);
  // Notification add to cart success JSX
  const addToCartSuccessMessage = () => (
    <>
      <StyledLeftNotification>{t('Đã thêm vào giỏ')}</StyledLeftNotification>
      <StyledRightNotification
        data-track-content
        data-content-region-name="toastViewCart"
        data-content-name="toastViewCart"
        data-content-target="shoppingCart"
        onClick={() => router.push('/cart').then(() => window.scrollTo(0, 0))}
      >
        <StyledSvgIcon name="shopping-cart" color="#ffffff" size={18} />
        {t('Xem giỏ')}
      </StyledRightNotification>
    </>
  );
  const refetch = (
    withLoading = false,
    onSuccess?: () => void,
  ): Promise<any> => {
    if (!defaultParamsCart.terminal) {
      return;
    }
    if (withLoading) {
      setLoadingCart(true);
    }
    return cartService
      .getCart(defaultParamsCart)
      .then((res: Cart) => {
        setCart(cookCart(res));
        if (!!onSuccess) onSuccess();
      })
      .finally(() => setLoadingCart(false));
  };
  const orderLineSkus = (cart?.orderLines || []).map(
    ({ sku }: CartOrderLine) => ({
      sku,
    }),
  );
  useEffect(() => {
    const { cartToken } = router.query;
    if (!!cartToken) {
      setCartToken(cartToken as string);
    }
  }, []);
  useEffect(() => {
    (async () => {
      if (changePriceFeature) {
        if (userAddress && orderLineSkus.length > 0) {
          await cartService.setShippingAddress(
            { terminal, channel },
            userAddress,
          );
          const services: ApplicableService[] =
            await cartService.getApplicableServices({
              terminal,
              channel,
              onlySelectedItems: false,
            });
          const applicableSkus =
            services.find(
              ({ service }) => service.id === CONSULTATION_SERVICE_ID,
            )?.applicableSkus || [];
          setAllowChangePriceProducts(applicableSkus);
        }
      }
    })();
  }, [JSON.stringify(orderLineSkus), channel, terminal, userAddress]);
  const isEmpty =
    cart === undefined ? undefined : cart?.orderLines?.length === 0;
  const selectedPartOrderPromotion = useMemo(() => {
    if (!cart?.promotions) {
      return null;
    }
    const partOrderPromos = cart.promotions.filter(
      (el) =>
        ['order', 'part_order'].includes(el.applyType) &&
        el.coupon &&
        el.basePromotion.isDefault,
    );
    return (
      partOrderPromos
        .map(({ sellerIds, basePromotion }) => {
          return {
            sellerId: sellerIds?.[0],
            promotionId: basePromotion.id.toString(),
            coupon: basePromotion.condition.coupon,
            orderValueMin: basePromotion.condition.orderValueMin || 0,
            orderValueMax: basePromotion.condition.orderValueMax || 0,
            benefit: basePromotion.benefit,
            disabled: false,
          };
        })
        .shift() || null
    );
  }, [cart?.promotions]);
  /**
   * Get applied coupons in promotions block of api get cart
   */
  const appliedCoupons =
    cart?.promotions
      .filter(
        (promotion) =>
          ['order', 'part_order'].includes(promotion.applyType) &&
          !!promotion.coupon &&
          !!promotion.discount &&
          !promotion.basePromotion.isDefault,
      )
      .map((promotion) => {
        const coupon = cart.orderCoupons.applicableCoupons.find(
          (coupon) => coupon.condition.coupon === promotion.coupon,
        );
        return (
          !!coupon && {
            amount: promotion.discount,
            ...coupon,
          }
        );
      })
      .filter((coupon) => !!coupon) || [];
  /**
   * Get applied e-voucher by remapping order coupons from e-vouchers block
   */
  const appliedEVouchers =
    cart?.eVouchers
      .map((eVoucher) => {
        const eVoucherCode = cart.orderCoupons.applicableCoupons.find(
          (coupon) => coupon.condition.coupon === eVoucher.voucherCode,
        );
        return (
          !!eVoucherCode && {
            amount: eVoucher.amount,
            appliedAmount: eVoucher.appliedAmount,
            ...eVoucherCode,
          }
        );
      })
      .filter((eVoucher) => !!eVoucher) || [];
  const order = useMemo(() => {
    if (!cart || Object.keys(cart).length === 0) {
      return undefined;
    }
    const orderLines = cart.orderLines
      .filter(({ isGift }) => !isGift)
      .map((line) => {
        if (line.product?.productInfo && !line.product.productInfo.imageUrl) {
          line.product.productInfo.imageUrl = skuImageMapping[line.sku];
        }
        return {
          ...line,
          isPriceEditable: allowChangePriceProducts.includes(line.sku),
        };
      });

    const promotions = cart.promotions.map((promo) => {
      if (promo.applyType === 'product')
        return {
          ...promo,
          gifts: promo.gifts.map((item: CartGift) => ({
            ...item,
            imageUrl: item.imageUrl || skuImageMapping[item.sku],
          })),
        };

      return promo;
    });
    const promotionsMap = promotions
      .filter(({ applyType }) => applyType === 'product')
      .reduce((acc, curr) => {
        const output: PromotionsMap = {
          ...acc,
        };
        curr.applyOn.forEach(({ lineItemId }: any) => {
          if (!output[lineItemId]) {
            output[lineItemId] = {
              gifts: [],
            };
          }
          output[lineItemId].gifts = output[lineItemId].gifts.concat(
            curr.gifts,
          );
          if (curr.voucher) {
            output[lineItemId].voucher = curr.voucher;
          }
        });

        return output;
      }, {});
    const shippingFeeDiscount =
      cart.promotions?.find(
        ({ applyType, applyOn }) =>
          applyType === 'service-fee' &&
          cart?.appliedServices?.find(
            (item) => item.id === applyOn?.[0]?.serviceId,
          ),
      )?.discount || 0;
    const promotionsDiscount = (cart?.promotions || []).reduce(
      (total, promo) =>
        total +
        ((promo.applyType !== 'product' &&
          promo.applyType !== 'combo' &&
          promo.applyType !== 'service-fee' &&
          promo.discount) ||
          0),
      0,
    );
    const eVouchersDiscount =
      cart?.eVouchers?.reduce((acc, { amount }) => acc + amount, 0) || 0;
    return {
      ...cart,
      eVouchersDiscount,
      promotionsDiscount,
      /*
       * I use + instead of || to handle a case where there is order promotion (applyType = order)
       * setup on product. which is not allowed currently on phongvu.
       */
      totalDiscount: eVouchersDiscount + promotionsDiscount,
      orderLines,
      shippingFeeDiscount: shippingFeeDiscount,
      sellerLines: groupCartBySeller(orderLines, cart.promotions),
      promotionsMap,
    };
  }, [cart, JSON.stringify(allowChangePriceProducts)]);

  const comboProducts =
    cart?.orderLines.filter((line: CartOrderLine) => line.childItems.length) ||
    [];
  const applicablePolicies = cart?.applicablePolicies || [];
  const appliedServices = cart?.appliedServices || [];
  const minOrderAmountToCheckout = cart?.minOrderAmountToCheckout || 0;
  const selectedComboProduct = comboProducts.filter(
    (product) => product.isSelected,
  );
  const totalSelectedComboProducts = selectedComboProduct.length;
  const comboGrandTotal = selectedComboProduct.reduce(
    (acc, product) => acc + product.rowTotal,
    0,
  );
  const comboBenefits = !!comboProducts.length
    ? getComboBenefits(comboProducts, cart.promotions)
    : [];
  const [suggestedCoupons, partOrderPromotions] = useMemo(() => {
    if (!cart?.orderCoupons) {
      return [[], []];
    }
    const coupons = [
      ...cart?.orderCoupons.applicableCoupons.map((coupon) => ({
        ...coupon,
        disabled: false,
      })),
      ...cart?.orderCoupons.inapplicableCoupons.map((coupon) => ({
        ...coupon,
        disabled: true,
      })),
    ].filter((coupon) => !coupon.isPrivate);
    const partOrderPromos = coupons
      .filter((coupon) => coupon.isDefault && coupon.condition.coupon)
      .map((coupon) => {
        return {
          sellerId: coupon.applySellerIds[0],
          promotionId: coupon.id.toString(),
          coupon: coupon.condition.coupon,
          orderValueMin: coupon.condition.orderValueMin || 0,
          orderValueMax: coupon.condition.orderValueMax || 0,
          benefit: coupon.benefit,
          disabled: coupon.disabled,
        };
      })
      .filter(
        ({ promotionId }) =>
          promotionId !== selectedPartOrderPromotion?.promotionId,
      );
    //move selected promotion to first
    if (selectedPartOrderPromotion) {
      partOrderPromos.unshift(selectedPartOrderPromotion);
    }
    return [
      coupons
        .filter((coupon) => !coupon.isDefault)
        .map((coupon) => {
          return {
            text: coupon.condition.coupon || '',
            detail: coupon.name,
            disabled: coupon.disabled,
            type: coupon.type,
            promotionId: coupon.id.toString(),
            orderValueMin: coupon.condition.orderValueMin || 0,
            orderValueMax: coupon.condition.orderValueMax || 0,
            benefit: coupon.benefit,
            skus: coupon.condition.skus,
            sellerId: coupon.sellerId,
            applyOn: coupon.applyOn,
            endedAt: coupon.endedAt ? new Date(coupon.endedAt) : null,
            applySellerIds: coupon?.applySellerIds,
            isDefault: coupon.isDefault,
          };
        }),
      partOrderPromos,
    ];
  }, [cart?.orderCoupons, selectedPartOrderPromotion]);
  const getPartOrderDiscountText = () => {
    const sellerName =
      order.sellerLines.find(
        ({ seller }) => seller.id === selectedPartOrderPromotion.sellerId,
      )?.seller?.displayName || '';
    return t('Giảm giá (khuyến mãi đơn hàng {{sellerName}})', {
      sellerName: sellerName,
    });
  };
  const totalPartOrderDiscount = !!selectedPartOrderPromotion
    ? cart.promotions
        .filter(
          (el) =>
            ['order', 'part_order'].includes(el.applyType) &&
            el.coupon &&
            el.basePromotion.isDefault,
        )
        .shift()?.discount
    : 0;
  const partOrderDiscountText =
    !!totalPartOrderDiscount && order.sellerLines?.length
      ? getPartOrderDiscountText()
      : '';
  const giftSkus = [
    ...new Set(
      [...(cart?.promotions || []), ...partOrderPromotions].reduce(
        (total, current: any) => [
          ...total,
          ...(
            current?.basePromotion?.benefit?.gifts ||
            current?.benefit?.gifts ||
            []
          ).map(({ sku }) => sku),
        ],
        [],
      ),
    ),
  ]
    .filter((sku: string) => !giftsImgMap[sku])
    .sort();
  // totalPriceDifferences is total increase price of Umbala
  // if (is Umbla consultationServiceFee is valid) return value
  // else return 0
  const totalPriceDifferences =
    cart?.orderLines
      ?.map((orderLine) =>
        orderLine.isSelected && orderLine.consultationServiceFee
          ? orderLine.consultationServiceFee * orderLine.quantityDecimal
          : 0,
      )
      .reduce((prevValue, currentValue) => currentValue + prevValue, 0) || 0;

  useEffect(() => {
    (async () => {
      if (giftSkus && giftSkus.length > 0) {
        const products = await discoveryService.getProductDetailBySkus({
          skus: giftSkus.join(','),
          terminalCode: terminal,
          location,
        });
        const imagesMap = products.reduce(
          (total, { productInfo: { sku, imageUrl } }) => ({
            ...total,
            [sku]: imageUrl,
          }),
          {},
        );
        setGiftsImgMap((prev) => ({ ...prev, ...imagesMap }));
      }
    })();
  }, [JSON.stringify(giftSkus)]);

  useEffect(() => {
    if (!isFetchingIAMState)
      refetch(true, () => {
        setIsPostLoginRefetch(true);
      });
  }, [isFetchingIAMState]);

  useEffect(() => {
    if (
      forceLoginBeforeApplyCoupon &&
      user &&
      !loadingCart &&
      isPostLoginRefetch
    ) {
      const coupon = localStorage.getItem(LOCALSTORAGE_CART_APPLY_COUPON);
      if (coupon) {
        onApplyCoupon(coupon);
      }
    }
  }, [user, loadingCart, forceLoginBeforeApplyCoupon, isPostLoginRefetch]);

  const handleCartMessage = (e: any) => {
    if (isMobile) {
      mobileToast.info({
        message: e?.message,
      });
    } else {
      desktopToast.info({
        title: e?.message,
        message: null,
      });
    }
  };
  const isAppliedCouponConflicting = (errorCode: number): boolean => {
    return [
      PROMOTION_OVERRIDE_COUPON_CODE,
      COUPON_OVERRIDE_PROMOTION_CODE,
      ...COUPON_CONFLICT_PART_ORDER_AND_COMBO_CODES,
    ].includes(errorCode);
  };
  const getConflictingPromotionWarning = (error: ApiError): ReactNode => {
    return [
      PROMOTION_OVERRIDE_COUPON_CODE,
      COUPON_OVERRIDE_PROMOTION_CODE,
    ].includes(error?.code)
      ? getOverrideModalBody(
          getConflictCouponId(error?.response?.error),
          error?.code,
        )
      : error?.message || t('CartContext.0');
  };
  const postProcessingApplyCoupon = () => {
    if (user) localStorage.removeItem(LOCALSTORAGE_CART_APPLY_COUPON);
    refetch(true);
  };
  const confirmApplyCoupon = async (coupon: string) => {
    setLoadingCartDiscount(true);
    try {
      if (forceLoginBeforeApplyCoupon && !user) {
        localStorage.setItem(LOCALSTORAGE_CART_APPLY_COUPON, coupon);
        authService.login(`${baseDomain}/cart`);
      } else {
        // below catch block handle error codes of this api call
        await cartService.applyCoupon(defaultParamsCart, {
          coupon,
          override: false,
        });
        if (isMobile) {
          showToast(<InfoToast message={t('Áp dụng mã thành công')} />);
        }
      }
      postProcessingApplyCoupon();
    } catch (error) {
      if (isAppliedCouponConflicting(error?.code)) {
        setSubstituteCouponModal({
          show: true,
          body: getConflictingPromotionWarning(error),
          onApply: () => {
            confirmPartOrderPromotion(coupon, true, () =>
              setSubstituteCouponModal({ show: false }),
            );
          },
        });
      } else if (
        error.response?.statusCode === PLATFORM_NOT_ALLOW_MULTI_EVOUCHER_CODE
      ) {
        const popupMessage = t('CartContext.1');
        setSubstituteCouponModal({
          show: true,
          body: popupMessage,
          onApply: () => {
            cartService
              .applyCoupon(defaultParamsCart, { coupon, override: true })
              .then(() => {
                setSubstituteCouponModal({ show: false });
                if (isMobile) {
                  showToast(<InfoToast message={t('Áp dụng mã thành công')} />);
                }
                postProcessingApplyCoupon();
              });
          },
        });
      } else {
        setCouponError(error.message);
        postProcessingApplyCoupon();
      }
    } finally {
      setLoadingCartDiscount(false);
    }
  };
  const getOverrideModalBody = (promotionId: string, code: number) => {
    let msg = '';
    if (code === PROMOTION_OVERRIDE_COUPON_CODE) {
      const coupon = cart.orderCoupons.applicableCoupons.find(
        (coupon) => coupon.id.toString() === promotionId,
      )?.condition?.coupon;
      msg = t(
        'Khuyến mãi này không thể áp dụng cùng mã giảm giá {{coupon}}. Bạn có chắc muốn bỏ áp dụng mã giảm giá để nhận khuyến mãi này?',
        { coupon: coupon },
      );
    } else {
      const promotion = cart.promotions.find(
        (promotion) => promotion.basePromotion.id.toString() === promotionId,
      );
      const { flat, percent, maxAmount } =
        promotion.basePromotion.benefit.discount;
      const reason = flat
        ? formatCurrency(flat)
        : t('{{percent}}% tối đa {{formatCurrency(maxAmount)}}', {
            percent: percent,
            'formatCurrency(maxAmount)': formatCurrency(maxAmount),
          });
      msg = t(
        'Mã giảm giá không thể áp dụng cùng với khuyến mãi đơn hàng giảm {{reason}}. Bạn có chắc chắn muốn xóa khuyến mãi này để áp dụng mã giảm giá?',
        { reason: reason },
      );
    }
    return msg;
  };
  const confirmPartOrderPromotion = async (
    coupon: string,
    override?: boolean,
    onSuccess?: () => void,
  ) => {
    setLoadingCartDiscount(true);
    try {
      await cartService.applyCoupon(defaultParamsCart, {
        coupon,
        override: override || false,
      });
      if (!!onSuccess) onSuccess();
      refetch(true);
    } catch (error) {
      if (isAppliedCouponConflicting(error?.code)) {
        setSubstituteCouponModal({
          show: true,
          body: getConflictingPromotionWarning(error),
          onApply: () => {
            confirmPartOrderPromotion(coupon, true, () => {
              setSubstituteCouponModal({ show: false });
              if (!!onSuccess) onSuccess();
            });
          },
        });
      } else {
        const { message } = error;
        setSubstituteCouponModal({
          show: true,
          body: message,
        });
        refetch(true);
      }
    } finally {
      setLoadingCartDiscount(false);
    }
  };
  const getConflictCouponId = (message = ''): string => {
    return message.split(' ').pop();
  };
  const onApplyCoupon = async (coupon: string) => {
    await confirmApplyCoupon(coupon);
  };
  const onRemoveCoupon = async (payload?: boolean | string): Promise<void> => {
    setLoadingCartDiscount(true);
    try {
      if (typeof payload === 'string') {
        // new api version
        await cartService.removeCoupon(defaultParamsCart, { coupon: payload });
      } else {
        // backwards compatible with old api version
        await cartService.removeCoupon(defaultParamsCart, {
          defaultCoupon: !!payload,
        });
      }
    } finally {
      setLoadingCartDiscount(false);
      refetch();
    }
  };
  const onAddToCart = async (
    body: AddToCartReq,
    fromProductCard = false,
  ): Promise<any> => {
    if (fromProductCard && trackingScreenName) {
      trackManually(
        'addToCard',
        'productCard',
        trackingScreenName,
        JSON.stringify({
          sku: body.sku,
          screenName: trackingScreenName,
          index: 0,
        }),
      );
    }
    let res;
    try {
      setLoadingAddToCart(true);
      res = await cartService.addToCart(defaultParamsCart, body);
      if (isMobile) {
        showToast(<InfoToast message={addToCartSuccessMessage()} />);
      } else {
        desktopToast.success({
          title: 'Đã thêm sản phẩm vào giỏ hàng',
          message: null,
        });
      }
      return {
        ...res,
        cartId: cart?.id || res.cartId,
      };
    } catch (e: any) {
      handleCartMessage(e);
      return Promise.reject(e);
    } finally {
      refetch(true);
      setLoadingAddToCart(false);
    }
  };
  const onAddMultiToCart = async (body: AddToCartReq[]): Promise<any> => {
    try {
      setLoadingAddToCart(true);
      const res = await cartService.addToCart(defaultParamsCart, {
        groups: [{ products: body }],
      });
      if (isMobile) {
        showToast(<InfoToast message={addToCartSuccessMessage()} />);
      } else {
        desktopToast.success({
          title: 'Đã thêm sản phẩm vào giỏ hàng',
          message: null,
        });
      }
      return res;
    } catch (e: any) {
      handleCartMessage(e);
      return null;
    } finally {
      refetch(true);
      setLoadingAddToCart(false);
    }
  };
  const onUpdateOrderLine = async (id: string, body: UpdateOrderLineReq) => {
    try {
      setLoadingAddToCart(true);
      await cartService.updateOrderLine(defaultParamsCart, id, body);
    } catch (e: any) {
      handleCartMessage(e);
    } finally {
      refetch(true);
      setLoadingAddToCart(false);
    }
  };
  const onRemoveItem = async (orderLine: CartOrderLine) => {
    trackRemoveFromCart(orderLine, cart.id);
    setLoadingAddToCart(true);
    return cartService
      .removeOrderLine(defaultParamsCart, orderLine.id)
      .then(() => refetch(true))
      .catch((error) => {
        logger.error(error);
        handleCartMessage(error);
        refetch(true);
      })
      .finally(() => setLoadingAddToCart(false));
  };
  const onIncrease = (orderLine: CartOrderLine) => {
    trackIncreaseQuantityProduct(orderLine, null, cart.id);
    return onUpdateOrderLine(orderLine.id, {
      quantity: orderLine.quantity + 1,
      selected: orderLine.isSelected,
    });
  };
  const onDecrease = (orderLine: CartOrderLine) => {
    trackDecreaseQuantityProduct(orderLine, null, cart.id);
    return onUpdateOrderLine(orderLine.id, {
      quantity: orderLine.quantity - 1,
      selected: orderLine.isSelected,
    });
  };
  const onChangeQuantity = (orderLine: CartOrderLine, newQuantity: number) => {
    trackChangeQuantityProduct(orderLine, newQuantity, cart.id);
    return onUpdateOrderLine(orderLine.id, {
      ...(Number.isInteger(newQuantity)
        ? { quantity: newQuantity }
        : { quantityDecimal: newQuantity }),
    });
  };
  const onSelectOrderLine = (orderLine: CartOrderLine) => {
    trackSelectProduct(orderLine, !orderLine.isSelected, cart.id);
    return onUpdateOrderLine(orderLine.id, {
      selected: !orderLine.isSelected,
    });
  };
  const onDeleteCart = async () => {
    return cartService.clearCart(defaultParamsCart).then(() => refetch());
  };
  const onSelectSeller = async (sellerId: number, selected: boolean) => {
    return cartService
      .updateSellerItems(defaultParamsCart, sellerId, selected)
      .then(() => refetch());
  };
  const onSelectCombo = async () => {
    if (!comboProducts.length) return;
    comboProducts.map((product) => {
      onUpdateOrderLine(product.id, {
        selected: !product.isSelected,
        quantity: product.quantity,
      });
    });
    return;
  };
  const applyServiceToCart = async (body?: ApplyServiceCart): Promise<void> => {
    setLoadingCartDiscount(true);
    await cartService.applyServiceToCart(
      {
        terminal,
        channel,
      },
      body,
    );
    await refetch();
    setLoadingCartDiscount(false);
  };
  const applyPaymentMethodToCard = async (
    body?: ApplyPaymentMethodCart,
  ): Promise<void> => {
    setLoadingCartDiscount(true);
    await cartService.applyPaymentMethodToCard(
      {
        terminal,
        channel,
      },
      body,
    );
    await refetch();
    setLoadingCartDiscount(false);
  };
  const applyShippingAddress = async (addr: Address): Promise<void> => {
    await cartService.setShippingAddress({ terminal, channel }, addr);
    await refetch();
  };
  return (
    <CartContext.Provider
      value={{
        applicablePolicies,
        appliedServices,
        minOrderAmountToCheckout,
        order,
        paymentMethodCode: cart?.paymentMethodCode,
        loading: loadingCart,
        totalCartItemsSelected: order
          ? order.orderLines.filter(
              (orderLine) => orderLine.isSelected && !orderLine.parentSku,
            ).length
          : 0,
        suggestedCoupons,
        partOrderPromotions,
        selectedPartOrderPromotion,
        totalPartOrderDiscount,
        partOrderDiscountText,
        appliedCoupons,
        appliedEVouchers,
        totalPriceDifferences,
        refetch,
        onRemoveCoupon,
        onApplyCoupon,
        confirmPartOrderPromotion,
        cartDiscountLoading: loadingCartDiscount,
        onAddToCart,
        onAddMultiToCart,
        onUpdateOrderLine,
        giftsImgMap,
        onSelectSeller,
        onRemoveItem,
        onDeleteCart,
        onDecrease,
        onIncrease,
        onChangeQuantity,
        onSelectOrderLine,
        onSelectCombo,
        isEmpty,
        loadingAddToCart,
        isAllowChangePrice: allowChangePriceProducts?.length > 0,
        comboProducts,
        totalSelectedComboProducts,
        comboGrandTotal,
        comboBenefits,
        preCalculatedPoints: cart?.preCalculatedPoints,
        customer: cart?.customer,
        applyServiceToCart,
        applyPaymentMethodToCard,
        applyShippingAddress,
        get appliedDeliveryService() {
          return appliedServices.find(
            (service) => !!service.deliveryAttributes,
          );
        },
        get installService() {
          return appliedServices.find(
            (service) => service.id === TECHNICAL_SUPPORT_SERVICE_ID,
          );
        },
        get totalServiceFee() {
          // total applied service fee
          const appliedServiceFee =
            appliedServices.reduce((sum, cur) => {
              return cur.fee + sum;
            }, 0) || 0;

          // totalServiceFee = total applied service fee - shipping fee discount
          return appliedServiceFee - (order?.shippingFeeDiscount || 0);
        },
        get subTotalAfterLineDiscount() {
          return (
            order?.grandTotal -
            this.totalServiceFee +
            (order?.promotionsDiscount || 0)
          );
        },
      }}
    >
      <CustomModal
        size="sm"
        show={!!couponError}
        onHide={(): void => setCouponError('')}
      >
        <ModalHeader>{t('Có lỗi xảy ra')}</ModalHeader>
        <ModalBody>
          <CouponErrorMessage>{couponError}</CouponErrorMessage>
        </ModalBody>
        <ModalFooter>
          <StyledButton
            variant="normal"
            onClick={(): void => setCouponError('')}
          >
            {t('Đóng')}
          </StyledButton>
        </ModalFooter>
      </CustomModal>
      <ApplyCouponModal
        show={substituteCouponModal.show}
        onHide={(): void =>
          setSubstituteCouponModal({
            show: false,
            body: '',
            onApply: () => {},
          })
        }
        size="sm"
      >
        <StyledModalHeader>{t('Thông báo')}</StyledModalHeader>
        <StyledModalBody>{substituteCouponModal.body}</StyledModalBody>
        <StyledModalFooter>
          {substituteCouponModal.onApply && (
            <StyledSecondaryButton
              onClick={(): void =>
                setSubstituteCouponModal({
                  show: false,
                  body: '',
                  onApply: () => {},
                })
              }
            >
              {t('Bỏ qua')}
            </StyledSecondaryButton>
          )}
          <StyledPrimaryButton
            variant="primary"
            onClick={(): void => {
              substituteCouponModal.onApply
                ? substituteCouponModal.onApply()
                : setSubstituteCouponModal({
                    show: false,
                    body: '',
                    onApply: () => {},
                  });
            }}
          >
            {substituteCouponModal.onApply ? t('Áp dụng') : t('Đóng')}
          </StyledPrimaryButton>
        </StyledModalFooter>
      </ApplyCouponModal>
      {children}
    </CartContext.Provider>
  );
};
export function useCart(): CartContextState {
  const context = useContext<any>(CartContext);
  if (!context) {
    throw new Error('useCart must be inside a CartProvider with a state value');
  }
  ModalFooter;
  return context;
}
